import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faChartLine,
    faDatabase,
    faFileExport,
    faHome,
    faCompress,
    faExpand,
    faInfoCircle,
    faWeightHanging,
    faBolt
} from '@fortawesome/free-solid-svg-icons';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import SearchBar from '../../components/common/elements/searchBar';
import './Dashboard.scss';
import { provisioningService } from '../../services/provisioningServices';
import { getEventCategory } from './deviceEventCategories';

const CONFIG = {
    refreshInterval: 30000,
    networkCheckInterval: 5000,
    networkCheckUrl: 'https://www.google.com',
    connectionThresholds: {
        slight_delay: 15 * 60 * 1000,
        delay: 30 * 60 * 1000,
        not_live: 3 * 60 * 60 * 1000
    },
    malfunctionValues: {
        TEMPERATURE: 999.9,
        VOLUME: 888.8,
        MOTOR: ['8,8,8,8', '8888', '888.8'],
        POWER: ['8,8,8,8', '8888', '888.8'],
        COMP: ['8,8,8,8', '8888', '888.8'],
        WEIGHT: 99999.88,
        ENERGY: ['8,8,8,8', '8888', '888.8', '999.9', '888.8']
    }
};

const isEnergyDevice = (deviceName, productType, eventType) => {
    if (!deviceName && !productType && !eventType) return false;
    
    const nameUpper = deviceName ? deviceName.toUpperCase() : '';
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';

    if (nameUpper.includes('FLOW') && nameUpper.includes('METER')) {
        return false;
    }

    return (
        nameUpper.includes('ENERGY') ||
        (nameUpper.includes('METER') && !nameUpper.includes('FLOW')) ||
        nameUpper.includes('KWH') ||
        nameUpper.includes('POWER METER') ||
        typeUpper.includes('ENERGY') ||
        typeUpper.includes('METER') ||
        eventUpper === 'ENERGY' ||
        eventUpper === 'KWH' ||
        eventUpper === 'POWER'
    );
};

const isWeightDevice = (deviceName, productType, eventType) => {
    if (!deviceName && !productType && !eventType) return false;
    
    const nameUpper = deviceName ? deviceName.toUpperCase() : '';
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';
    
    return (
        nameUpper.includes('WEIGHT') ||
        nameUpper.includes('WEIGH') ||
        typeUpper.includes('WEIGHT') ||
        typeUpper.includes('WEIGH') ||
        eventUpper === 'WEIGHT'
    );
};

const isFanDevice = (deviceName, eventType) => {
    if (!deviceName) return false;
    const nameUpper = deviceName.toUpperCase();
    const eventUpper = eventType ? eventType.toUpperCase() : '';
    
    return (
        (nameUpper === 'ID FAN' || nameUpper === 'FD FAN') &&
        eventUpper === 'MOTOR'
    );
};

const isCompDevice = (deviceName, productType, eventType) => {
    const nameUpper = deviceName ? deviceName.toUpperCase() : '';
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';

    if (nameUpper.includes('TEMP') || eventUpper === 'TEMPERATURE') {
        return false;
    }

    // Check productType or eventType as primary indicators
    if (typeUpper.includes('COMP') || typeUpper.includes('COMPRESSOR') ||
        eventUpper.includes('COMP') || eventUpper.includes('COMPRESSOR') ||
        eventUpper === 'COMPRESSOR_STATUS' || eventUpper === 'COMP_STATUS') {
        return !nameUpper.includes('AGZ');
    }

    // Also check device name as secondary indicator
    return nameUpper.includes('COMP') && !nameUpper.includes('AGZ');
};

const isAGZDevice = (deviceName, productType, eventType) => {
    if (!deviceName) return false;
    const nameUpper = deviceName.toUpperCase();
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';

    if (nameUpper.includes('TEMP') || eventUpper === 'TEMPERATURE') {
        return false;
    }

    return nameUpper.includes('AGZ') || eventUpper === 'AGZ' || typeUpper === 'AGZ';
};

const isPowerDevice = (deviceName, productType, eventType) => {
    const nameUpper = deviceName ? deviceName.toUpperCase() : '';
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';

    return (
        nameUpper === 'DG' ||
        nameUpper === 'GRID' ||
        typeUpper === 'POWER' ||
        typeUpper.includes('DG') ||
        typeUpper.includes('GRID') ||
        eventUpper === 'DG' ||
        eventUpper === 'GRID' ||
        eventUpper === 'DG_STATUS' ||
        eventUpper === 'GRID_STATUS' ||
        eventUpper.includes('DG') ||
        eventUpper.includes('GRID')
    );
};

const isMotorDevice = (deviceName, productType, eventType) => {
    const nameUpper = deviceName ? deviceName.toUpperCase() : '';
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';

    if (nameUpper.includes('TEMP') || eventUpper === 'TEMPERATURE') {
        return false;
    }

    return (
        nameUpper.includes('MOTOR') ||
        typeUpper === 'MOTOR' ||
        typeUpper.includes('MOTOR') ||
        eventUpper === 'MOTOR' ||
        eventUpper === 'MOTOR_STATUS' ||
        eventUpper.includes('MOTOR')
    );
};

const isTemperatureSensor = (deviceName, productType, eventType) => {
    if (!deviceName) return false;
    const nameUpper = deviceName.toUpperCase();
    const typeUpper = productType ? productType.toUpperCase() : '';
    const eventUpper = eventType ? eventType.toUpperCase() : '';
    
    const isExplicitTemp = 
        nameUpper.includes('TEMP') ||
        eventUpper === 'TEMPERATURE' ||
        typeUpper === 'TEMPERATURE' ||
        nameUpper.includes('TEMPERATURE');
    
    const isMixedTempDevice = 
        (nameUpper.includes('COMP') && nameUpper.includes('TEMP')) ||
        (nameUpper.includes('MOTOR') && nameUpper.includes('TEMP')) ||
        (nameUpper.includes('POWER') && nameUpper.includes('TEMP'));
    
    return isExplicitTemp || isMixedTempDevice;
};

const getDashboardData = async (plantName) => {
    try {
        const response = await fetch(`/processingservice/dashboard/v2/sectionsData/?plantName=${encodeURIComponent(plantName)}`);
        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
    }
};

const isFlowmeterDevice = (deviceName, eventType) => {
    const category = getEventCategory(eventType);
    if (category) return category === 'flowmeter';
    return !!deviceName && deviceName.toLowerCase().includes('flowmeter');
};

const getFlowmeterData = async (deviceUUID, productUUID, deviceName = "flowmeter") => {
    if (!productUUID) {
        console.error('productUUID is required for flowmeter data');
        throw new Error('productUUID is required');
    }
    if (!deviceUUID) {
        console.error('deviceUUID is required for flowmeter data');
        throw new Error('deviceUUID is required');
    }
    
    try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        
        const currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        
        const response = await provisioningService.getFlowmeterLiveEvent({
            deviceUUID,
            productUUID,
            deviceName,
            deviceType: "flowmeter",
            time: currentTime
        });
                
        if (!response || !response.content) {
            console.warn("Invalid flowmeter response structure");
            return {
                content: {
                    todaysConsumptionData: { state: 'NOT_LIVE', todayConsumption: 0, unit: 'L' },
                    lastRecentHourData: { lastHourConsumption: 0, unit: 'L' },
                    previousDayData: { previousDayConsumption: 0, unit: 'L' },
                    sameDayLastMonthData: { consumptionSinceSameDayLastMonth: 0, unit: 'KL' }
                }
            };
        }
        
        return response;
    } catch (error) {
        console.error('Error fetching flowmeter data:', error);
        return {
            content: {
                todaysConsumptionData: { state: 'NOT_LIVE', todayConsumption: 0, unit: 'L' },
                lastRecentHourData: { lastHourConsumption: 0, unit: 'L' },
                previousDayData: { previousDayConsumption: 0, unit: 'L' },
                sameDayLastMonthData: { consumptionSinceSameDayLastMonth: 0, unit: 'KL' }
            }
        };
    }
};

const extractProductUUIDFromDevice = (device, product) => {
    return device.productUUID ||
        product.productUUID ||
        product.productId ||
        device.productId ||
        null;
};

const formatEnergyValue = (value, unit = 'kWh') => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    
    if (unit === 'kWh' || unit === 'kWh') {
        if (numValue >= 1000) {
            return `${Math.round(numValue).toLocaleString()} ${unit}`;
        }
        return `${numValue.toFixed(2)} ${unit}`;
    }
    
    if (unit === 'kW' || unit === 'kW') {
        return `${numValue.toFixed(2)} ${unit}`;
    }
    
    if (unit === 'A' || unit === 'A') {
        return `${numValue.toFixed(2)} ${unit}`;
    }
    
    return `${numValue} ${unit}`;
};

const Dashboard = () => {
    const [allDevices, setAllDevices] = useState([]);
    const [locations, setLocations] = useState([]);
    const [productTypes, setProductTypes] = useState([]);
    const [isOnline, setIsOnline] = useState(true);
    const [networkAlert, setNetworkAlert] = useState({ show: false, message: '', type: '' });
    const [currentFilters, setCurrentFilters] = useState({
        location: '',
        productType: 'all',
        tempStatus: 'all',
        connectionStatus: 'all',
        search: '',
        disableTempStatus: false,
        disableConnectionStatus: false
    });
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [lastUpdatedTime, setLastUpdatedTime] = useState();
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0, visible: false, deviceId: null });
    const [flowmeterData, setFlowmeterData] = useState({});
    const [energyMeterData, setEnergyMeterData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
  if (window.clarity) {
    window.clarity('event', 'page_viewed');
  }
}, []);

    // Helper functions defined at the top
    const getDefaultFlowmeterData = () => ({
        content: {
            todaysConsumptionData: { state: 'NOT_LIVE', todayConsumption: 0, unit: 'L' },
            lastRecentHourData: { lastHourConsumption: 0, unit: 'L' },
            previousDayData: { previousDayConsumption: 0, unit: 'L' },
            sameDayLastMonthData: { consumptionSinceSameDayLastMonth: 0, unit: 'KL' }
        }
    });

    const getDefaultEnergyMeterData = () => ({
        content: {
            todaysConsumptionData: { 
                value: 0, 
                unit: 'kWh', 
                status: 'NOT_LIVE',
                sessionStartTime: null 
            },
            currentPower: { value: 0, unit: 'kW', status: 'NOT_LIVE' },
            lastHourConsumption: { value: 0, unit: 'kWh', status: 'NOT_LIVE' },
            previousDayEMConsumption: { value: 0, unit: 'kWh', status: 'NOT_LIVE' },
            Average_Current: { value: 0, unit: 'A', status: 'NOT_LIVE' },
            Active_Energy: { value: 0, unit: 'kWh', status: 'NOT_LIVE' },
            Average_Power_Factor: { value: 0, unit: 'per unit', status: 'NOT_LIVE' }
        }
    });

    const getEnergyMeterData = async (deviceUUID, productUUID, deviceName = "ENERGYMETER 1") => {
        if (!productUUID) {
            console.error('productUUID is required for energy meter data');
            throw new Error('productUUID is required');
        }
        if (!deviceUUID) {
            console.error('deviceUUID is required for energy meter data');
            throw new Error('deviceUUID is required');
        }

        if (deviceName && deviceName.toLowerCase().includes('flow')) {
            console.log(`Skipping energy meter API for flowmeter: ${deviceName}`);
            return null;
        }

        try {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            
            const currentTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            
            console.log("Energy Meter request time:", currentTime);
            console.log("Energy Meter request params:", { deviceUUID, productUUID, deviceName });
            
            const response = await provisioningService.getEnergyMeterLiveData({
                deviceUUID,
                productUUID,
                deviceName,
                deviceType: "energy_meter",
                time: currentTime
            });
            
            console.log("Energy Meter API response:", response);
            
            if (!response) {
                console.warn("Empty energy meter response");
                return null;
            }
            
            // If response already has content property
            if (response.content) {
                return response;
            }

            // If response has todaysLiveConsumptionData directly
            if (response.todaysLiveConsumptionData) {
                return {
                    content: response
                };
            }

            // If response has the structure we need
            if (response.lastRecentHourConsumptionData || response.instantElectricalAttributes || response.todaysLiveConsumptionData) {
                return {
                    content: response
                };
            }

            // Check for any of the expected properties
            if (response.todaysLiveConsumptionData || 
                response.lastHourConsumption !== undefined || 
                response.previousDayEMConsumption !== undefined || 
                response.Average_Current !== undefined) {
                return {
                    content: response
                };
            }
            
            console.warn("Unknown energy meter response structure:", response);
            return null;

        } catch (error) {
            console.error('Error fetching energy meter data:', error);
            return null;
        }
    };

    const formatDateTime = (isoString) => {
        try {
            const date = new Date(isoString);
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (e) {
            return 'N/A';
        }
    };

    const toggleDeviceInfo = (deviceId, event) => {
        if (event) {
            const rect = event.currentTarget.getBoundingClientRect();
            const popupWidth = 320;
            
            let x = rect.left + window.scrollX;
            let y = rect.top + window.scrollY - 100;
            
            if (x + popupWidth > window.innerWidth) {
                x = window.innerWidth - popupWidth - 20;
            }
            
            if (y < 10) {
                y = 10;
            }
            
            setPopupPosition({
                x,
                y,
                visible: popupPosition.deviceId !== deviceId || !popupPosition.visible,
                deviceId
            });
        } else {
            setPopupPosition(prev => ({ ...prev, visible: false }));
        }
    };

    const showNetworkAlert = (message, type) => {
        setNetworkAlert({ show: true, message, type });
        setTimeout(() => {
            setNetworkAlert(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    const checkNetworkStatus = useCallback(async () => {
        if (!navigator.onLine) {
            handleNetworkChange(false);
            return;
        }

        try {
            await fetch(CONFIG.networkCheckUrl, {
                method: 'HEAD',
                cache: 'no-store',
                mode: 'no-cors'
            });
            if (!isOnline) handleNetworkChange(true);
        } catch {
            if (isOnline) handleNetworkChange(false);
        }
    }, [isOnline]);

    const handleNetworkChange = (online) => {
        if (isOnline === online) return;
        
        setIsOnline(online);
        
        if (online) {
            showNetworkAlert('Connection restored - syncing data...', 'success');
            loadDashboardData();
        } else {
            showNetworkAlert('Network connection lost - using cached data', 'danger');
        }
    };

    const isSensorMalfunction = (value, productType, deviceName = '', eventType = '') => {
        if (!value) return false;
        
        const upperType = productType ? productType.toUpperCase() : '';
        const numValue = parseFloat(value);
        
        // Check for energy meter malfunction
        if (isEnergyDevice(deviceName, productType, eventType)) {
            const upperValue = value.toString().toUpperCase();
            if (CONFIG.malfunctionValues.ENERGY.includes(upperValue)) {
                return true;
            }
            // Check for numerical malfunction values
            if (!isNaN(numValue)) {
                if (Math.abs(numValue - 999.9) < 0.1 || 
                    Math.abs(numValue - 888.8) < 0.1) {
                    return true;
                }
            }
        }
        
        if (isWeightDevice(deviceName, productType, eventType) && !isNaN(numValue)) {
            if (Math.abs(numValue - CONFIG.malfunctionValues.WEIGHT) < 0.1) {
                return true;
            }
        }

        if (upperType !== 'MOTOR' && upperType !== 'POWER' && upperType !== 'COMP' && upperType !== 'WATER VOLUME' &&
            !isWeightDevice(deviceName, productType, eventType) && !isEnergyDevice(deviceName, productType, eventType)) {
            if (!isNaN(numValue) && Math.abs(numValue - CONFIG.malfunctionValues.TEMPERATURE) < 0.1) {
                return true;
            }
        }
        
        if (upperType === 'WATER VOLUME') {
            if (!isNaN(numValue) && numValue === CONFIG.malfunctionValues.VOLUME) {
                return true;
            }
        }
        
        if (isCompDevice(deviceName, productType, eventType) ||
            isAGZDevice(deviceName, productType, eventType) ||
            isMotorDevice(deviceName, productType, eventType) ||
            isPowerDevice(deviceName, productType, eventType)) {
            const upperValue = value.toUpperCase();

            // Check for malfunction values
            if (CONFIG.malfunctionValues.MOTOR.includes(upperValue) ||
                CONFIG.malfunctionValues.POWER.includes(upperValue) ||
                CONFIG.malfunctionValues.COMP.includes(upperValue)) {
                return true;
            }
            if (isFanDevice(deviceName, eventType)) {
                if (upperValue !== 'ON' && upperValue !== 'OFF' && upperValue !== 'NOT CONF') {
                    return true;
                }
            } else {
                if (upperValue !== 'ON' && upperValue !== 'OFF') {
                    return true;
                }
            }
        }
        
        return false;
    };

    const getTemperatureStatus = (temp, minRange, maxRange) => {
        if (temp > maxRange) return 'critical';
        if (temp < minRange) return 'critical';
        return 'normal';
    };

    const getWeightStatus = (weight, minRange, maxRange) => {
        if (weight > maxRange) return 'critical';
        if (weight < minRange) return 'critical';
        return 'normal';
    };

    const getConnectionStatus = (timeDiff) => {
        if (!timeDiff) return 'na';
        
        if (timeDiff > CONFIG.connectionThresholds.not_live) {
            return 'not_live';
        } else if (timeDiff > CONFIG.connectionThresholds.delay) {
            return 'delay';
        } else if (timeDiff > CONFIG.connectionThresholds.slight_delay) {
            return 'slight_delay';
        }
        return 'live';
    };

    const processApiData = useCallback((sections) => {
        const devices = [];
        const newLocations = [];
        const newProductTypes = [];
        const now = new Date();

        const energyDeviceIds = new Set();
        sections.forEach(section => (section.productsList || []).forEach(product =>
            (product.devicesEventList || []).forEach(device => {
                if (device.deviceSerialNumber && device.deviceName && getEventCategory(device.eventType) === 'energymeter') {
                    energyDeviceIds.add(`${device.deviceSerialNumber}-${device.deviceName}`);
                }
            })
        ));

        sections.forEach(section => {
            if (!section.sectionName || !section.productsList) return;

            if (!newLocations.includes(section.sectionName)) {
                newLocations.push(section.sectionName);
            }

            section.productsList.forEach(product => {
                if (!product.productName || !product.devicesEventList) return;

                if (product.productType) {
                    const upperType = product.productType.toUpperCase();
                    if (!newProductTypes.includes(upperType)) {
                        newProductTypes.push(upperType);
                    }
                }

                product.devicesEventList.forEach(device => {
                    if (!device.deviceSerialNumber || !device.deviceName) return;

                    // Use only unique device identifiers, NOT location (which can change)
                    const deviceId = `${device.deviceSerialNumber}-${device.deviceName}`;

                    if (energyDeviceIds.has(deviceId) && getEventCategory(device.eventType) !== 'energymeter') return;

                    const minRange = device.minRange;
                    const maxRange = device.maxRange;

                    const isNAValue = device.eventValue === 'N/A' || device.eventValue === null || device.eventValue === undefined;
                    const isNATime = device.occurredTime === 'N/A' || device.occurredTime === null || device.occurredTime === undefined;

                    const apiDeviceStatus = device.deviceStatus ? device.deviceStatus.toLowerCase() : null;

                    const isVolumeType = device.eventType && device.eventType.toUpperCase() === 'VOLUME';
                    const eventCategory = getEventCategory(device.eventType);
                    const isWeightDeviceType = isWeightDevice(device.deviceName, product.productType, device.eventType);
                    const isFanDeviceType = isFanDevice(device.deviceName, device.eventType);
                    const isCompDeviceType = eventCategory ? eventCategory === 'comp' : isCompDevice(device.deviceName, product.productType, device.eventType);
                    const isAGZDeviceType = eventCategory ? eventCategory === 'agz' : isAGZDevice(device.deviceName, product.productType, device.eventType);
                    const isMotorDeviceType = eventCategory ? eventCategory === 'motor' : isMotorDevice(device.deviceName, product.productType, device.eventType);
                    const isPowerDeviceType = eventCategory ? (eventCategory === 'dg' || eventCategory === 'grid') : isPowerDevice(device.deviceName, product.productType, device.eventType);
                    const isEnergyDeviceType = eventCategory ? eventCategory === 'energymeter' : isEnergyDevice(device.deviceName, product.productType, device.eventType);
                    const isTempSensorType = eventCategory ? eventCategory === 'temperature' : isTemperatureSensor(device.deviceName, product.productType, device.eventType);

                    // Special handling for Energy Meter devices - show cumulative energy from dashboard API
                    if (isEnergyDeviceType) {
                        const energyValue = parseFloat(device.eventValue);
                        const unit = device.unit || 'kWh';

                        if (isNAValue || isNATime) {
                            devices.push({
                                id: deviceId,
                                name: device.deviceName,
                                serialNumber: device.deviceSerialNumber,
                                location: section.sectionName,
                                productName: product.productName || 'N/A',
                                productType: product.productType,
                                productUUID: extractProductUUIDFromDevice(device, product),
                                deviceUUID: device.deviceUUID,
                                eventType: device.eventType,
                                displayValue: 'N/A',
                                status: 'na',
                                connectionStatus: 'na',
                                lastUpdated: device.occurredTime,
                                deviceStatus: apiDeviceStatus || 'na',
                                minRange: minRange,
                                maxRange: maxRange,
                                isWeightDevice: false,
                                isFanDevice: false,
                                isCompDevice: false,
                                isMotorDevice: false,
                                isPowerDevice: false,
                                isEnergyDevice: true,
                                rawEventValue: device.eventValue,
                                energyValue: energyValue,
                                unit: unit,
                                isTempSensor: false
                            });
                            return;
                        }

                        const lastUpdated = device.occurredTime ? new Date(device.occurredTime) : null;
                        const timeDiff = lastUpdated ? now - lastUpdated : null;
                        const eventValueStr = device.eventValue ? device.eventValue.toString() : '';

                        let displayValue, status;

                        if (isSensorMalfunction(eventValueStr, product.productType, device.deviceName, device.eventType)) {
                            displayValue = 'SENSOR MALFUNCTION';
                            status = 'sensor-malfunction';
                        }
                        else if (apiDeviceStatus) {
                            status = apiDeviceStatus;
                            displayValue = isNaN(energyValue)
                                ? 'SENSOR MALFUNCTION'
                                : formatEnergyValue(energyValue, unit);
                        }
                        else {
                            if (isNaN(energyValue)) {
                                displayValue = 'SENSOR MALFUNCTION';
                                status = 'sensor-malfunction';
                            } else {
                                displayValue = formatEnergyValue(energyValue, unit);
                                // For energy cumulative values, default to normal status
                                status = 'normal';
                            }
                        }

                        const connectionStatus = !timeDiff ? 'na' : getConnectionStatus(timeDiff);

                        devices.push({
                            id: deviceId,
                            name: device.deviceName,
                            serialNumber: device.deviceSerialNumber,
                            location: section.sectionName,
                            productName: product.productName || 'N/A',
                            productType: product.productType,
                            productUUID: extractProductUUIDFromDevice(device, product),
                            deviceUUID: device.deviceUUID,
                            eventType: device.eventType,
                            displayValue: displayValue,
                            status: status,
                            connectionStatus: connectionStatus,
                            deviceStatus: apiDeviceStatus || status,
                            minRange: minRange,
                            maxRange: maxRange,
                            lastUpdated: device.occurredTime,
                            isWeightDevice: false,
                            isFanDevice: false,
                            isCompDevice: false,
                            isMotorDevice: false,
                            isPowerDevice: false,
                            isEnergyDevice: true,
                            rawValue: eventValueStr,
                            rawEventValue: device.eventValue,
                            energyValue: energyValue,
                            unit: unit,
                            isTempSensor: false
                        });
                        return;
                    }

                    // Rest of the devices (non-energy)
                    if (isNAValue || isNATime) {
                        devices.push({
                            id: deviceId,
                            name: device.deviceName,
                            serialNumber: device.deviceSerialNumber,
                            location: section.sectionName,
                            productName: product.productName || 'N/A',
                            productType: product.productType,
                            productUUID: extractProductUUIDFromDevice(device, product),
                            deviceUUID: device.deviceUUID,
                            eventType: device.eventType,
                            displayValue: 'N/A',
                            status: 'na',
                            connectionStatus: 'na',
                            lastUpdated: device.occurredTime,
                            deviceStatus: apiDeviceStatus || 'na',
                            minRange: minRange,
                            maxRange: maxRange,
                            isWeightDevice: isWeightDeviceType,
                            isFanDevice: isFanDeviceType,
                            isCompDevice: isCompDeviceType,
                            isAGZDevice: isAGZDeviceType,
                            isMotorDevice: isMotorDeviceType,
                            isPowerDevice: isPowerDeviceType,
                            isEnergyDevice: false,
                            rawEventValue: device.eventValue,
                            isTempSensor: isTempSensorType
                        });
                        return;
                    }

                    const lastUpdated = device.occurredTime ? new Date(device.occurredTime) : null;
                    const timeDiff = lastUpdated ? now - lastUpdated : null;
                    const eventValueStr = device.eventValue ? device.eventValue.toString() : '';

                    let displayValue, status, connectionStatus;

                    if (isWeightDeviceType) {
                        const weightValue = parseFloat(eventValueStr);

                        if (isSensorMalfunction(eventValueStr, product.productType, device.deviceName, device.eventType)) {
                            displayValue = 'SENSOR MALFUNCTION';
                            status = 'sensor-malfunction';
                        }
                        else if (apiDeviceStatus) {
                            status = apiDeviceStatus;
                            displayValue = isNaN(weightValue)
                                ? 'SENSOR MALFUNCTION'
                                : `${weightValue.toFixed(2)} KG`;
                        }
                        else {
                            if (isNaN(weightValue)) {
                                displayValue = 'SENSOR MALFUNCTION';
                                status = 'sensor-malfunction';
                            } else {
                                displayValue = `${weightValue.toFixed(2)} KG`;
                                status = getWeightStatus(weightValue, minRange, maxRange);
                            }
                        }
                    }
                    else if (isSensorMalfunction(eventValueStr, product.productType, device.deviceName, device.eventType)) {
                        displayValue = 'SENSOR MALFUNCTION';
                        status = 'sensor-malfunction';
                    }
                    else if (apiDeviceStatus) {
                        status = apiDeviceStatus;

                        if (isVolumeType || (product.productType && product.productType.toLowerCase() === 'volume')) {
                            const volumeValue = parseFloat(eventValueStr);
                            displayValue = isNaN(volumeValue)
                                ? 'SENSOR MALFUNCTION'
                                : `${volumeValue.toFixed(1)} L`;
                        }
                        else if (isTempSensorType) {
                            // Handle temperature sensors (including COMP 1-2 TEMP, COMP 3-4 TEMP)
                            const temp = parseFloat(eventValueStr);
                            displayValue = isNaN(temp)
                                ? 'SENSOR MALFUNCTION'
                                : `${temp.toFixed(1)} °C`;
                        }
                        else if (isFanDeviceType) {
                            const fanStatus = eventValueStr.toUpperCase();
                            displayValue = fanStatus;
                        }
                        else if (isCompDeviceType || isAGZDeviceType || isMotorDeviceType || isPowerDeviceType) {
                            const powerStatus = eventValueStr.toUpperCase();
                            displayValue = powerStatus;
                        }
                        else {
                            const temp = parseFloat(eventValueStr);
                            displayValue = isNaN(temp)
                                ? 'SENSOR MALFUNCTION'
                                : `${temp.toFixed(1)} °C`;
                        }
                    }
                    else {
                        if (isVolumeType || (product.productType && product.productType.toLowerCase() === 'volume')) {
                            const volumeValue = parseFloat(eventValueStr);
                            displayValue = isNaN(volumeValue)
                                ? 'SENSOR MALFUNCTION'
                                : `${volumeValue.toFixed(1)} L`;
                            status = isNaN(volumeValue)
                                ? 'sensor-malfunction'
                                : getTemperatureStatus(volumeValue, minRange, maxRange);
                        }
                        else if (isTempSensorType) {
                            // Handle temperature sensors (including COMP 1-2 TEMP, COMP 3-4 TEMP)
                            const temp = parseFloat(eventValueStr);
                            displayValue = isNaN(temp)
                                ? 'SENSOR MALFUNCTION'
                                : `${temp.toFixed(1)} °C`;
                            status = isNaN(temp)
                                ? 'sensor-malfunction'
                                : getTemperatureStatus(temp, minRange, maxRange);
                        }
                        else if (isFanDeviceType) {
                            const fanStatus = eventValueStr.toUpperCase();
                            displayValue = fanStatus;
                            status = 'sensor-malfunction';
                        }
                        else if (isCompDeviceType || isAGZDeviceType || isMotorDeviceType || isPowerDeviceType) {
                            const powerStatus = eventValueStr.toUpperCase();
                            displayValue = powerStatus;
                            status = powerStatus === 'ON'
                                ? 'normal'
                                : powerStatus === 'OFF'
                                    ? 'critical'
                                    : 'sensor-malfunction';
                        }
                        else {
                            const temp = parseFloat(eventValueStr);
                            displayValue = isNaN(temp)
                                ? 'SENSOR MALFUNCTION'
                                : `${temp.toFixed(1)} °C`;
                            status = isNaN(temp)
                                ? 'sensor-malfunction'
                                : getTemperatureStatus(temp, minRange, maxRange);
                        }
                    }

                    connectionStatus = !timeDiff ? 'na' : getConnectionStatus(timeDiff);

                    devices.push({
                        id: deviceId,
                        name: device.deviceName,
                        serialNumber: device.deviceSerialNumber,
                        location: section.sectionName,
                        productName: product.productName || 'N/A',
                        productType: product.productType,
                        productUUID: extractProductUUIDFromDevice(device, product),
                        deviceUUID: device.deviceUUID,
                        eventType: device.eventType,
                        displayValue: displayValue,
                        status: status,
                        connectionStatus: connectionStatus,
                        deviceStatus: apiDeviceStatus || status,
                        minRange: minRange,
                        maxRange: maxRange,
                        lastUpdated: device.occurredTime,
                        isWeightDevice: isWeightDeviceType,
                        isFanDevice: isFanDeviceType,
                        isCompDevice: isCompDeviceType,
                        isAGZDevice: isAGZDeviceType,
                        isMotorDevice: isMotorDeviceType,
                        isPowerDevice: isPowerDeviceType,
                        isEnergyDevice: false,
                        rawValue: eventValueStr,
                        rawEventValue: device.eventValue,
                        isTempSensor: isTempSensorType
                    });
                });
            });
        });

        setAllDevices(devices);
        setLocations(newLocations.sort());
        setProductTypes(newProductTypes.sort());

        return devices;
    }, []);

    const processFlowmeterData = useCallback(async (devices) => {
        // Filter only flowmeter devices
        const flowmeterDevices = devices.filter(device =>
            isFlowmeterDevice(device.name, device.eventType)
        );

        console.log(`Found ${flowmeterDevices.length} flowmeter devices`);

        const flowmeterDataMap = {};
        
        for (const device of flowmeterDevices) {
            try {
                if (!device.productUUID || !device.deviceUUID) {
                    console.warn(`Missing UUIDs for flowmeter ${device.id}:`, {
                        productUUID: device.productUUID,
                        deviceUUID: device.deviceUUID
                    });
                    flowmeterDataMap[device.id] = getDefaultFlowmeterData();
                    continue;
                }
                
                const data = await getFlowmeterData(device.deviceUUID, device.productUUID, device.name);
                
                flowmeterDataMap[device.id] = processAndRoundFlowmeterData(data);

            } catch (error) {
                console.error(`Error fetching data for flowmeter ${device.id}:`, error);
                flowmeterDataMap[device.id] = getDefaultFlowmeterData();
            }
        }
        
        setFlowmeterData(flowmeterDataMap);
    }, []);

    const processEnergyMeterData = useCallback(async (devices) => {
        // Filter only energy meter devices (exclude flowmeters)
        const energyMeterDevices = devices.filter(device =>
            device.isEnergyDevice &&
            !(device.name && device.name.toLowerCase().includes('flow'))
        );

        console.log(`Found ${energyMeterDevices.length} energy meter devices (excluding flowmeters)`);

        const energyMeterDataMap = {};
        
        for (const device of energyMeterDevices) {
            try {
                if (!device.productUUID || !device.deviceUUID) {
                    console.warn(`Missing UUIDs for energy meter ${device.id}:`, {
                        productUUID: device.productUUID,
                        deviceUUID: device.deviceUUID
                    });
                    energyMeterDataMap[device.id] = getDefaultEnergyMeterData();
                    continue;
                }
                
                const data = await getEnergyMeterData(device.deviceUUID, device.productUUID, device.name);

                if (data) {
                    energyMeterDataMap[device.id] = processAndRoundEnergyMeterData(data);
                } else {
                    energyMeterDataMap[device.id] = getDefaultEnergyMeterData();
                }

            } catch (error) {
                console.error(`Error fetching data for energy meter ${device.id}:`, error);
                energyMeterDataMap[device.id] = getDefaultEnergyMeterData();
            }
        }
        
        setEnergyMeterData(energyMeterDataMap);
    }, []);

    const processAndRoundFlowmeterData = (data) => {
        if (!data || !data.content) return getDefaultFlowmeterData();
        
        const content = data.content;
        
        // Extract data with proper mapping
        const todaysData = content.todaysConsumptionData || {};
        const lastHourData = content.lastRecentHourData || {};
        const prevDayData = content.previousDayData || {};
        const lastMonthData = content.sameDayLastMonthData || {};
        
        // Parse values
        const todayConsumption = todaysData.todayConsumption !== undefined && todaysData.todayConsumption !== null 
            ? parseFloat(todaysData.todayConsumption) 
            : 0;
        
        const lastHourConsumption = lastHourData.lastHourConsumption !== undefined && lastHourData.lastHourConsumption !== null 
            ? parseFloat(lastHourData.lastHourConsumption) 
            : 0;
        
        const previousDayConsumption = prevDayData.previousDayConsumption !== undefined && prevDayData.previousDayConsumption !== null 
            ? parseFloat(prevDayData.previousDayConsumption) 
            : 0;
        
        const consumptionSinceSameDayLastMonth = lastMonthData.consumptionSinceSameDayLastMonth !== undefined && lastMonthData.consumptionSinceSameDayLastMonth !== null 
            ? parseFloat(lastMonthData.consumptionSinceSameDayLastMonth) 
            : 0;
        
        // Determine status based on state field or if value exists
        const todayStatus = todaysData.state || (todayConsumption > 0 ? 'LIVE' : 'NOT_LIVE');
        const lastHourStatus = lastHourData.lastHourConsumption !== undefined && lastHourData.lastHourConsumption !== null ? 'LIVE' : 'NOT_LIVE';
        const prevDayStatus = prevDayData.previousDayConsumption !== undefined && prevDayData.previousDayConsumption !== null ? 'LIVE' : 'NOT_LIVE';
        const lastMonthStatus = lastMonthData.consumptionSinceSameDayLastMonth !== undefined && lastMonthData.consumptionSinceSameDayLastMonth !== null ? 'LIVE' : 'NOT_LIVE';
        
        return {
            content: {
                todaysConsumptionData: {
                    status: todayStatus,
                    todayConsumption: roundToTwoDecimals(todayConsumption),
                    unit: todaysData.unit || 'KL',
                    sessionStartTime: todaysData.todayConsumptionSessionStartTime
                },
                lastRecentHourData: {
                    status: lastHourStatus,
                    lastHourConsumption: roundToTwoDecimals(lastHourConsumption),
                    unit: lastHourData.unit || 'L',
                    sessionEndTime: lastHourData.lastHourSessionEndTime
                },
                previousDayData: {
                    status: prevDayStatus,
                    previousDayConsumption: roundToTwoDecimals(previousDayConsumption),
                    unit: prevDayData.unit || 'L',
                    sessionDate: prevDayData.lastDaySessionDate
                },
                sameDayLastMonthData: {
                    status: lastMonthStatus,
                    consumptionSinceSameDayLastMonth: roundToTwoDecimals(consumptionSinceSameDayLastMonth),
                    unit: lastMonthData.unit || 'KL',
                    sessionEndTime: lastMonthData.consumptionSinceSameDayLastMonthSessionEndTime
                }
            }
        };
    };

    const processAndRoundEnergyMeterData = (data) => {
        if (!data || !data.content) return getDefaultEnergyMeterData();

        const content = data.content;

        // Extract today's live consumption data
        const todaysLiveData = content.todaysLiveConsumptionData || {};
        
        // Extract last hour data
        const lastHourData = content.lastRecentHourConsumptionData || {};
        
        // Extract previous day data
        const previousDayData = content.previousDayData || {};
        
        // Extract instant attributes
        const instantAttributes = content.instantElectricalAttributes || {};

        // Get today's live consumption value - this is the important part
        const todayLiveValue = todaysLiveData.todaysLiveConsumption !== undefined ? 
                              parseFloat(todaysLiveData.todaysLiveConsumption) : 0;
        
        // Get other values
        const lastHourValue = lastHourData.lastHourConsumption;
        const previousDayValue = previousDayData.previousDayEMConsumption;
        
        // Get average current
        const averageCurrentValue = instantAttributes.Average_Current ? 
                                   instantAttributes.Average_Current.value : undefined;
        
        // Get active energy (convert Wh to kWh if needed)
        const activeEnergyValue = instantAttributes.Active_Energy ? 
                                 instantAttributes.Active_Energy.value : undefined;
        
        // Get power factor value
        const powerFactorValue = instantAttributes.POWER_FACTOR_AVG ? 
                                instantAttributes.POWER_FACTOR_AVG.value : undefined;
        
        // Fallback to PF_1, PF_2, PF_3 if POWER_FACTOR_AVG is not available
        const pf1 = instantAttributes.PF_1 ? instantAttributes.PF_1.value : undefined;
        const pf2 = instantAttributes.PF_2 ? instantAttributes.PF_2.value : undefined;
        const pf3 = instantAttributes.PF_3 ? instantAttributes.PF_3.value : undefined;

        // Calculate average power factor if available
        let avgPowerFactor = 0;
        let hasPowerFactor = false;
        
        // First try to use POWER_FACTOR_AVG if available
        if (powerFactorValue !== undefined) {
            const powerFactorNum = parseFloat(powerFactorValue);
            if (!isNaN(powerFactorNum)) {
                avgPowerFactor = powerFactorNum;
                hasPowerFactor = true;
            }
        } 
        // Fallback to calculating from PF_1, PF_2, PF_3
        else if (pf1 !== undefined && pf2 !== undefined && pf3 !== undefined) {
            const pf1Num = parseFloat(pf1);
            const pf2Num = parseFloat(pf2);
            const pf3Num = parseFloat(pf3);

            if (!isNaN(pf1Num) && !isNaN(pf2Num) && !isNaN(pf3Num)) {
                avgPowerFactor = ((pf1Num + pf2Num + pf3Num) / 3);
                hasPowerFactor = true;
            }
        }

        // Helper function to parse value with "N/A" handling
        const parseEnergyValue = (value) => {
            if (value === undefined || value === null || value === 'N/A' || value === '0.0' || value === '0') return 0;
            const num = parseFloat(value);
            return isNaN(num) ? 0 : num;
        };

        // Helper function to get status based on value
        const getEnergyStatus = (value) => {
            if (value === undefined || value === null || value === 'N/A' || value === '0.0') return 'NOT_LIVE';
            // If value exists and is not zero, it's LIVE
            if (parseFloat(value) > 0) return 'LIVE';
            return 'NOT_LIVE';
        };

        // Determine status for today's consumption
        const todayStatus = (todayLiveValue > 0) ? 'LIVE' : 
                           (todaysLiveData.state === 'LIVE' ? 'LIVE' : 'NOT_LIVE');

        return {
            content: {
                todaysConsumptionData: {
                    value: roundToTwoDecimals(todayLiveValue),
                    unit: todaysLiveData.unit || 'kWh',
                    status: todayStatus,
                    sessionStartTime: todaysLiveData.todaysSessionStartTime
                },
                currentPower: {
                    value: 0,
                    unit: 'kW',
                    status: 'NOT_LIVE'
                },
                lastHourConsumption: {
                    value: roundToTwoDecimals(parseEnergyValue(lastHourValue)),
                    unit: lastHourData.unit || 'kWh',
                    status: getEnergyStatus(lastHourValue)
                },
                previousDayEMConsumption: {
                    value: roundToTwoDecimals(parseEnergyValue(previousDayValue)),
                    unit: previousDayData.unit || 'kWh',
                    status: getEnergyStatus(previousDayValue)
                },
                Average_Current: {
                    value: roundToTwoDecimals(parseEnergyValue(averageCurrentValue)),
                    unit: instantAttributes.Average_Current ? 
                          (instantAttributes.Average_Current.unit === 'ampere' ? 'A' : instantAttributes.Average_Current.unit) : 'A',
                    status: getEnergyStatus(averageCurrentValue)
                },
                Active_Energy: {
                    value: roundToTwoDecimals(parseEnergyValue(activeEnergyValue) / 1000), // Convert Wh to kWh
                    unit: 'kWh',
                    status: getEnergyStatus(activeEnergyValue)
                },
                Average_Power_Factor: {
                    value: roundToTwoDecimals(avgPowerFactor),
                    unit: 'per unit',
                    status: hasPowerFactor ? 'LIVE' : 'NOT_LIVE'
                }
            }
        };
    };

    const roundToTwoDecimals = (value) => {
        if (value === null || value === undefined) return 0;
        const num = typeof value === 'number' ? value : parseFloat(value);
        
        if (isNaN(num)) return 0;
        
        return Math.floor(num * 100) / 100;
    };

    const loadDashboardData = useCallback(async () => {
        if (!isOnline) {
            showNetworkAlert('No network connection - using cached data', 'danger');
            return;
        }

        try {
            setIsLoading(true);
            const data = await getDashboardData("TEST PLANT ONE");
            const devices = processApiData(data.sections);
            await processFlowmeterData(devices);
            await processEnergyMeterData(devices);
            setLastUpdatedTime(new Date());
        } catch (error) {
            console.error('Error loading data:', error);
            showNetworkAlert('Failed to load data from server', 'danger');
        } finally {
            setIsLoading(false);
        }
    }, [isOnline, processApiData, processFlowmeterData, processEnergyMeterData]);

    useEffect(() => {
        if (currentFilters.location && !locations.includes(currentFilters.location)) {
            setCurrentFilters(prev => ({ ...prev, location: '' }));
        }
    }, [locations, currentFilters.location]);

    const applyFilters = useCallback(() => {
        const seenIds = new Set();
        const filtered = allDevices.filter(device => {
            const searchTerm = (currentFilters.search || '').trim().toLowerCase();

            // Search filter - search by device name, product name, and section/location
            let matchesSearch = true;
            if (searchTerm !== '') {
                matchesSearch =
                    (device.name && device.name.toLowerCase().includes(searchTerm)) ||
                    (device.productName && device.productName.toLowerCase().includes(searchTerm)) ||
                    (device.location && device.location.toLowerCase().includes(searchTerm));
            }

            const matchesLocation = currentFilters.location === '' ||
                (device.location && device.location.toLowerCase() === currentFilters.location.toLowerCase());

            const matchesProductType = currentFilters.productType === 'all' ||
                (device.productType && device.productType.toUpperCase() === currentFilters.productType.toUpperCase());

            const matchesTempStatus = currentFilters.disableTempStatus ||
                currentFilters.tempStatus === 'all' ||
                (device.status && device.status === currentFilters.tempStatus);

            const matchesConnectionStatus = currentFilters.disableConnectionStatus ||
                currentFilters.connectionStatus === 'all' ||
                (device.connectionStatus && device.connectionStatus === currentFilters.connectionStatus);

            const filters = matchesSearch && matchesLocation && matchesProductType && matchesTempStatus && matchesConnectionStatus;

            // Deduplicate - only include first occurrence of each device ID (case-insensitive)
            const deviceIdLower = device.id ? device.id.toLowerCase() : '';
            if (!deviceIdLower || seenIds.has(deviceIdLower)) {
                return false;
            }
            seenIds.add(deviceIdLower);

            return filters;
        });

        // Log filter results for debugging
        if (currentFilters.search || currentFilters.location || currentFilters.productType !== 'all' || currentFilters.tempStatus !== 'all' || currentFilters.connectionStatus !== 'all') {
            console.log('Filters Applied:', {
                search: currentFilters.search,
                location: currentFilters.location,
                productType: currentFilters.productType,
                tempStatus: currentFilters.tempStatus,
                connectionStatus: currentFilters.connectionStatus,
                disableTempStatus: currentFilters.disableTempStatus,
                disableConnectionStatus: currentFilters.disableConnectionStatus,
                totalDevices: allDevices.length,
                filteredDevices: filtered.length
            });
        }

        return filtered;
    }, [allDevices, currentFilters]);

    const exportToExcel = () => {
        if (allDevices.length === 0) {
            showNetworkAlert('No data available to export', 'danger');
            return;
        }
        
        const formatExcelDate = (isoString) => {
            try {
                const date = new Date(isoString);
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                });
            } catch (e) {
                return 'N/A';
            }
        };

        const exportData = allDevices.map(device => {
            // Add energy meter specific data if available
            const energyInfo = energyMeterData[device.id];
            let todayConsumption = null;
            
            if (energyInfo && energyInfo.content && energyInfo.content.todaysConsumptionData) {
                todayConsumption = energyInfo.content.todaysConsumptionData;
            }
            
            return {
                'Serial Number': device.serialNumber || 'N/A',
                'Device Name': device.name || 'Unnamed Device',
                'Section': device.location || 'N/A',
                'Product Name': device.productName || 'N/A',
                'Product Type': device.productType || 'N/A',
                'Value': device.displayValue,
                'Today\'s Consumption': device.isEnergyDevice && todayConsumption ? 
                    `${todayConsumption.value} ${todayConsumption.unit}` : 'N/A',
                'Status': device.status === 'na' ? 'N/A' : device.status.toUpperCase(),
                'Connection Status': device.connectionStatus === 'na' ? 'N/A' : device.connectionStatus.toUpperCase().replace('_', ' '),
                'Min Range': device.minRange,
                'Max Range': device.maxRange,
                'LastUpdated': device.lastUpdated ? formatExcelDate(device.lastUpdated) : 'N/A',
                'deviceStatus': device.deviceStatus,
                'Device Type': device.isEnergyDevice ? 'Energy Meter' :
                    device.isWeightDevice ? 'Weight Scale' :
                        device.isCompDevice ? 'Compressor' :
                            device.isAGZDevice ? 'AGZ' :
                                device.isFanDevice ? 'Fan' :
                                    device.isMotorDevice ? 'Motor' :
                                        device.isPowerDevice ? 'Power' :
                                            device.isTempSensor ? 'Temperature Sensor' : 'General'
            };
        });
      
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Device Report');
        
        const now = new Date();
        const fileName = `MooMarkmpp2_Bidadi_Report_${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}.xlsx`;

        XLSX.writeFile(wb, fileName);
    };

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setCurrentFilters(prev => ({ ...prev, [name]: checked }));
        } else {
            if (name === 'tempStatus' && value === 'disable_all') {
                setCurrentFilters(prev => ({
                    ...prev,
                    tempStatus: 'all',
                    disableTempStatus: true
                }));
            } else if (name === 'connectionStatus' && value === 'disable_all') {
                setCurrentFilters(prev => ({
                    ...prev,
                    connectionStatus: 'all',
                    disableConnectionStatus: true
                }));
            } else {
                setCurrentFilters(prev => ({
                    ...prev,
                    [name]: value,
                    ...(name === 'tempStatus' && { disableTempStatus: false }),
                    ...(name === 'connectionStatus' && { disableConnectionStatus: false })
                }));
            }
        }
    };

    const handleSearchInput = (searchValue) => {
        // Handle direct value from SearchBar component (string or undefined)
        let value = '';
        if (typeof searchValue === 'string') {
            value = searchValue;
        } else if (searchValue && searchValue.target && searchValue.target.value) {
            value = searchValue.target.value;
        }
        setCurrentFilters(prev => ({ ...prev, search: value }));
    };

    const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
    };

    const renderRangePopup = () => {
        if (!popupPosition.visible) return null;
        
        const device = allDevices.find(d => d.id === popupPosition.deviceId);
        if (!device) return null;

        const isFlowmeter = isFlowmeterDevice(device.name, device.eventType);
        const isEnergyMeter = device.isEnergyDevice;

        const flowmeterInfo = flowmeterData[device.id] || {
            content: {
                todaysConsumptionData: { status: 'NOT_LIVE', todayConsumption: 0, unit: 'KL', sessionStartTime: null },
                lastRecentHourData: { status: 'NOT_LIVE', lastHourConsumption: 0, unit: 'L', sessionEndTime: null },
                previousDayData: { status: 'NOT_LIVE', previousDayConsumption: 0, unit: 'L', sessionDate: null },
                sameDayLastMonthData: { status: 'NOT_LIVE', consumptionSinceSameDayLastMonth: 0, unit: 'KL', sessionEndTime: null }
            }
        };

        const energyMeterInfo = energyMeterData[device.id] || getDefaultEnergyMeterData();

        return (
            <div
                className="range-popup"
                style={{
                    position: 'absolute',
                    left: `${popupPosition.x}px`,
                    top: `${popupPosition.y}px`,
                    zIndex: 1000
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="popup-content">
                    {isFlowmeter ? (
                        <div className="popup-section">
                            <div className="popup-subheader">Flowmeter Data</div>
                            <div><strong>Today's Consumption:</strong>
                                {flowmeterInfo.content.todaysConsumptionData.status === 'LIVE' ? 
                                    ` ${flowmeterInfo.content.todaysConsumptionData.todayConsumption} ${flowmeterInfo.content.todaysConsumptionData.unit}` : 
                                    ' NOT_LIVE'
                                }
                            </div>
                            
                            <div><strong>Last Hour:</strong>
                                {flowmeterInfo.content.lastRecentHourData.status === 'LIVE' ? 
                                    ` ${flowmeterInfo.content.lastRecentHourData.lastHourConsumption} ${flowmeterInfo.content.lastRecentHourData.unit}` : 
                                    ' NOT_LIVE'
                                }
                            </div>
                            <div><strong>Previous Day:</strong>
                                {flowmeterInfo.content.previousDayData.status === 'LIVE' ? 
                                    ` ${flowmeterInfo.content.previousDayData.previousDayConsumption} ${flowmeterInfo.content.previousDayData.unit}` : 
                                    ' NOT_LIVE'
                                }
                            </div>
                            <div><strong>Last Month:</strong>
                                {flowmeterInfo.content.sameDayLastMonthData.status === 'LIVE' ? 
                                    ` ${flowmeterInfo.content.sameDayLastMonthData.consumptionSinceSameDayLastMonth} ${flowmeterInfo.content.sameDayLastMonthData.unit}` : 
                                    ' NOT_LIVE'
                                }
                            </div>
                        </div>
                    ) : isEnergyMeter ? (
                        <div className="popup-section">
                            <div className="popup-subheader">Energy Meter</div>
                            <div><strong>Today's Consumption:</strong>
                                {energyMeterInfo.content.todaysConsumptionData.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.todaysConsumptionData.value} ${energyMeterInfo.content.todaysConsumptionData.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div>
                           
                            <div><strong>Last Hour Consumption:</strong>
                                {energyMeterInfo.content.lastHourConsumption.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.lastHourConsumption.value} ${energyMeterInfo.content.lastHourConsumption.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div>
                            <div><strong>Previous Day Consumption:</strong>
                                {energyMeterInfo.content.previousDayEMConsumption.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.previousDayEMConsumption.value} ${energyMeterInfo.content.previousDayEMConsumption.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div>
                            {/* <div><strong>Average Current:</strong>
                                {energyMeterInfo.content.Average_Current.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.Average_Current.value} ${energyMeterInfo.content.Average_Current.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div> */}
                            <div><strong>Active Energy:</strong>
                                {energyMeterInfo.content.Active_Energy.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.Active_Energy.value} ${energyMeterInfo.content.Active_Energy.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div>
                            <div><strong>Average Power Factor:</strong>
                                {energyMeterInfo.content.Average_Power_Factor.status === 'LIVE'
                                    ? ` ${energyMeterInfo.content.Average_Power_Factor.value} ${energyMeterInfo.content.Average_Power_Factor.unit}`
                                    : ' NOT_LIVE'
                                }
                            </div>
                        </div>
                    ) : (
                        <div className="popup-section">
                            <div><strong>Min Range:</strong> <span>{device.minRange || 'N/A'}</span></div>
                            <div><strong>Max Range:</strong> <span>{device.maxRange || 'N/A'}</span></div>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    useEffect(() => {
        const onlineHandler = () => handleNetworkChange(true);
        const offlineHandler = () => handleNetworkChange(false);
        
        window.addEventListener('online', onlineHandler);
        window.addEventListener('offline', offlineHandler);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        
        const networkCheckInterval = setInterval(checkNetworkStatus, CONFIG.networkCheckInterval);
        const dataRefreshInterval = setInterval(loadDashboardData, CONFIG.refreshInterval);
        const datetimeInterval = setInterval(() => setCurrentDateTime(new Date()), 1000);
        
        loadDashboardData();
        
        return () => {
            window.removeEventListener('online', onlineHandler);
            window.removeEventListener('offline', offlineHandler);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            clearInterval(networkCheckInterval);
            clearInterval(dataRefreshInterval);
            clearInterval(datetimeInterval);
        };
    }, [checkNetworkStatus, loadDashboardData]);

    const renderDevices = () => {
        const filteredDevices = applyFilters();
        
        if (!filteredDevices || filteredDevices.length === 0) {
            return <div className="no-devices">No devices found matching current filters</div>;
        }
        
        const sectionColors = {};
        let colorIndex = 1;
        
        filteredDevices.forEach(device => {
            if (!sectionColors[device.location]) {
                sectionColors[device.location] = `section-${colorIndex}`;
                colorIndex = colorIndex % 5 + 1;
            }
        });
        
        return filteredDevices.map(device => {
            if (!device) return null;
            
            const isFlowmeter = isFlowmeterDevice(device.name, device.eventType);
            const isFanDeviceType = device.isFanDevice;
            const isCompDeviceType = device.isCompDevice;
            const isAGZDeviceType = device.isAGZDevice;
            const isMotorDeviceType = device.isMotorDevice;
            const isPowerDeviceType = device.isPowerDevice;
            const isEnergyMeterType = device.isEnergyDevice;
            const isTempSensorType = device.isTempSensor;

            const flowmeterInfo = flowmeterData[device.id] || {
                content: {
                    todaysConsumptionData: { status: 'NOT_LIVE', todayConsumption: 0, unit: 'KL' },
                    lastRecentHourData: { status: 'NOT_LIVE', lastHourConsumption: 0, unit: 'L' },
                    previousDayData: { status: 'NOT_LIVE', previousDayConsumption: 0, unit: 'L' },
                    sameDayLastMonthData: { status: 'NOT_LIVE', consumptionSinceSameDayLastMonth: 0, unit: 'KL' }
                }
            };

            const energyMeterInfo = energyMeterData[device.id] || getDefaultEnergyMeterData();

            const isCritical = device.status === 'critical' ||
                device.connectionStatus === 'not_live' ||
                device.deviceStatus === 'critical';
            const isMalfunction = device.status === 'sensor-malfunction';
            const showLastUpdated = device.lastUpdated && device.connectionStatus !== 'live' && !currentFilters.disableConnectionStatus;
            
            const status = device.deviceStatus || device.status;
            
            let valueClass, statusClass, statusText;
            
            if (status === 'na') {
                valueClass = 'not-available';
                statusClass = 'status-na';
                statusText = 'N/A';
            } 
            else if (status === 'sensor-malfunction') {
                valueClass = 'sensor-malfunction';
                statusClass = 'status-sensor-malfunction';
                statusText = 'SENSOR MALFUNCTION';
            }
            else if (isCompDeviceType || isAGZDeviceType || isFanDeviceType || isMotorDeviceType || isPowerDeviceType) {
                valueClass = device.displayValue === 'ON' ? 'power-on' : 'power-off';
                statusClass = `status-${status}`;
                statusText = status === 'normal' ? 'NORMAL' : 'CRITICAL';
            } 
            else if (isEnergyMeterType) {
                valueClass = currentFilters.disableTempStatus ? 'energy-normal' : `energy-${status}`;
                statusClass = currentFilters.disableTempStatus ? 'status-normal' : `status-${status}`;
                statusText = status.toUpperCase();
            }
            else if (device.isWeightDevice) {
                valueClass = currentFilters.disableTempStatus ? 'weight-normal' : `weight-${status}`;
                statusClass = currentFilters.disableTempStatus ? 'status-normal' : `status-${status}`;
                statusText = status.toUpperCase();
            }
            else if (isTempSensorType) {
                valueClass = currentFilters.disableTempStatus ? 'temp-normal' : `temp-${status}`;
                statusClass = currentFilters.disableTempStatus ? 'status-normal' : `status-${status}`;
                statusText = status.toUpperCase();
            }
            else {
                valueClass = currentFilters.disableTempStatus ? 'temp-normal' : `temp-${status}`;
                statusClass = currentFilters.disableTempStatus ? 'status-normal' : `status-${status}`;
                statusText = status.toUpperCase();
            }
            
            const sectionClass = sectionColors[device.location] || 'section-1';
            
            const showTodayConsumption = isEnergyMeterType && 
                                         energyMeterInfo && 
                                         energyMeterInfo.content && 
                                         energyMeterInfo.content.todaysConsumptionData && 
                                         energyMeterInfo.content.todaysConsumptionData.status !== 'NOT_LIVE';
            
            return (
                <div
                    key={device.id}
                    className={`device-card ${isCompDeviceType ? 'comp-device' : ''} ${isAGZDeviceType ? 'agz-device' : ''} ${isPowerDeviceType ? 'power-device' : ''} ${(isCritical || isMalfunction) && !currentFilters.disableTempStatus ? 'pulse-alert' : ''} ${device.isWeightDevice ? 'weight-device' : ''} ${isFanDeviceType ? 'fan-device' : ''} ${isMotorDeviceType ? 'motor-device' : ''} ${isEnergyMeterType ? 'energy-device' : ''} ${isTempSensorType ? 'temp-sensor-device' : ''}`}
                >
                    <div className="device-header">
                        <span className={`device-location ${sectionClass}`}>{device.location || 'N/A'}</span>
                        <div className="status-container">
                            {!currentFilters.disableTempStatus && (
                                <div className={`temp-status ${statusClass}`}>
                                    {statusText}
                                </div>
                            )}
                            <button
                                className="info-icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDeviceInfo(device.id, e);
                                }}
                                title="Show device information"
                            >
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </button>
                        </div>
                    </div>
                    <div className="device-product-name"><strong>{device.productName || 'N/A'}</strong></div>
                    <div className="device-name">
                        {device.name || 'Unnamed Device'}
                    </div>
                    
                    {isFlowmeter ? (
                        <div className={`temp-value ${valueClass}`}>
                            {flowmeterInfo && 
                             flowmeterInfo.content && 
                             flowmeterInfo.content.todaysConsumptionData && 
                             flowmeterInfo.content.todaysConsumptionData.status === 'LIVE' && 
                             flowmeterInfo.content.todaysConsumptionData.todayConsumption !== undefined && 
                             flowmeterInfo.content.todaysConsumptionData.todayConsumption !== null
                                ? `${flowmeterInfo.content.todaysConsumptionData.todayConsumption} ${flowmeterInfo.content.todaysConsumptionData.unit || 'KL'}`
                                : flowmeterInfo && 
                                  flowmeterInfo.content && 
                                  flowmeterInfo.content.todaysConsumptionData && 
                                  flowmeterInfo.content.todaysConsumptionData.status === 'NOT_LIVE'
                                    ? 'NOT_LIVE'
                                    : '0 KL'
                            }
                        </div>
                    ) : isEnergyMeterType ? (
                        <div className="energy-value-container">
                            <div className={`temp-value ${valueClass}`}>
                                {energyMeterInfo.content.todaysConsumptionData.value} {energyMeterInfo.content.todaysConsumptionData.unit}
                            </div>
                        </div>
                    ) : isCompDeviceType || isAGZDeviceType || isFanDeviceType || isMotorDeviceType || isPowerDeviceType ? (
                        <div className={`fan-display-value ${valueClass}`}>
                            {device.displayValue}
                        </div>
                    ) : (
                        <div
                            className={`temp-value ${valueClass}`}
                            style={status === 'sensor-malfunction' ? { fontSize: '1.2rem' } : {}}
                        >
                            {device.displayValue}
                        </div>
                    )}
                    
                    <div className="status-badges">
                        {showLastUpdated && (
                            <div className="last-updated">{formatDateTime(device.lastUpdated)}</div>
                        )}
                        {!currentFilters.disableConnectionStatus && (
                            <span className={`connection-status connection-${device.connectionStatus}`}>
                                {device.connectionStatus === 'na' ? 'N/A' : device.connectionStatus.replace('_', ' ').toUpperCase()}
                            </span>
                        )}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className="dashboard-container" onClick={() => setPopupPosition(prev => ({ ...prev, visible: false }))}>
            {networkAlert.show && (
                <div className={`network-alert ${networkAlert.type === 'danger' ? '' : 'online'}`}>
                    <i className={`fas ${networkAlert.type === 'danger' ? 'fa-exclamation-triangle' : 'fa-wifi'}`}></i>
                    <span className="alert-message">{networkAlert.message}</span>
                </div>
            )}
            
            <div className="dashboard-controls">
                <div className="filter-controls">
                    <div className="filter-group">
                        <SearchBar
                            onChange={handleSearchInput}
                            value={currentFilters.search}
                        />
                    </div>
                    <div className="filter-group">
                        <Autocomplete
                            id="locationFilter"
                            options={locations}
                            value={currentFilters.location || null}
                            onChange={(event, value) => {
                                handleFilterChange({ target: { name: 'location', value: value || '' } });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Sections" variant="outlined" size="small" />
                            )}
                            style={{ width: '100%' }}
                        />
                    </div>
                   
                    <div className="filter-group">
                        <Autocomplete
                            id="tempStatusFilter"
                            options={['All Alerts Type', 'Normal', 'Critical', 'N/A', 'Sensor Malfunction', 'Disable All Status']}
                            value={currentFilters.disableTempStatus ? 'Disable All Status' : (currentFilters.tempStatus === 'all' ? 'All Alerts Type' : currentFilters.tempStatus ? currentFilters.tempStatus.charAt(0).toUpperCase() + currentFilters.tempStatus.slice(1) : 'All Alerts Type')}
                            onChange={(event, value) => {
                                let filterValue = 'all';
                                if (value === 'Normal') filterValue = 'normal';
                                else if (value === 'Critical') filterValue = 'critical';
                                else if (value === 'N/A') filterValue = 'na';
                                else if (value === 'Sensor Malfunction') filterValue = 'sensor-malfunction';
                                else if (value === 'Disable All Status') filterValue = 'disable_all';

                                if (filterValue === 'disable_all') {
                                    handleFilterChange({ target: { name: 'disableTempStatus', value: true } });
                                } else {
                                    handleFilterChange({ target: { name: 'tempStatus', value: filterValue } });
                                }
                            }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Alerts Type" variant="outlined" size="small" />
                            )}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="filter-group">
                        <select
                            id="connectionStatusFilter"
                            name="connectionStatus"
                            onChange={handleFilterChange}
                            value={currentFilters.disableConnectionStatus ? 'disable_all' : currentFilters.connectionStatus}
                        >
                            <option value="all">All Connection States</option>
                            <option value="live">Live</option>
                            <option value="slight_delay">Slight Delay</option>
                            <option value="delay">Delay</option>
                            <option value="not_live">Not Live</option>
                            <option value="na">N/A</option>
                            <option value="disable_all">Disable All Connections</option>
                        </select>
                    </div>
                
                    <div className="view-buttons">
                        <button onClick={exportToExcel}>
                            <FontAwesomeIcon icon={faFileExport} /> Export
                        </button>
                    </div>
                </div>
            
                <div className="device-grid" id="deviceGrid">
                    {renderDevices()}
                </div>
            </div>
            {renderRangePopup()}
        </div>
    );
};

export default Dashboard;
